<script lang="ts">
	import classNames from 'classnames';
	import { getBookDisplayNames } from '#lib/features/publishing/utils/getBookDisplayNames/index.js';
	import { getBookPrices } from '#lib/features/publishing/utils/getBookPrices/getBookPrices.js';
	import { BookLabel } from '#lib/websites/publishing/components/BookLabel/index.js';
	import { PublishingRoutes } from '#lib/websites/publishing/constants/routes.js';
	import type { SearchBooksQuery } from '#lib/websites/publishing/queries/__generated__/searchBooks.generated.js';
	import { getBookLabelText } from '#lib/websites/publishing/utils/getBookLabel/getBookLabel.js';
	import { ArtworkCard } from '$global/components/ArtworkCard';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl/getImageUrl';

	interface Props {
		book: SearchBooksQuery['Publishing_Books'][0];
		dataCy: string;
		seriesUrlPrefix?: string;
		bookUrlPrefix?: string;
		onclick?: () => void;
	}

	let {
		book,
		dataCy,
		seriesUrlPrefix = PublishingRoutes.Series,
		bookUrlPrefix = PublishingRoutes.Home,
		onclick = undefined,
	}: Props = $props();

	let title = $derived(
		book?.series?.only_show_preview_in_gallery
			? book?.series?.title
			: book?.title
	);

	let { names } = $derived(getBookDisplayNames(book));

	let { price, previousPrice } = $derived(getBookPrices(book));
	let labelText = $derived(getBookLabelText(book));
</script>

<div class="relative">
	{#if labelText}
		<BookLabel class="absolute top-[8px] left-[8px] z-10 py-1">
			{labelText}
		</BookLabel>
	{/if}
	<ArtworkCard
		{dataCy}
		onClick={onclick}
		line1={title || ''}
		line2={names}
		href={book?.series
			? `${seriesUrlPrefix}/${book?.series?.id}`
			: `${bookUrlPrefix}/${book.slug}`}
		classes={{
			image: classNames(
				// Force a square card. Give the inner box a definite height (h-0) so
				// Safari resolves pb-[100%] against the width rather than collapsing it
				// on a centered flex child, then lay the image inside with object-contain.
				// No inset-0 here: twMerge treats it as a shorthand for top/left and
				// would strip the 50% offsets the centering transform needs.
				'h-auto p-0 [&>div]:h-0 [&>div]:pb-[100%] [&>div>img]:absolute [&>div>img]:top-[50%] [&>div>img]:left-[50%] [&>div>img]:transform-[translate(-50%,-50%)] [&>div>img]:object-contain'
			),
			imageContainer: 'h-auto',
		}}
		image={{
			width: book.featured_image?.width || 0,
			height: book.featured_image?.height || 0,
			url: getImageUrl(book.featured_image?.filename_disk, '512px') || '',
		}}
		alt={book.title || ''}
	>
		<Txt
			variant="label3"
			class="mt-1 text-gray-700"
			dataCy={`${dataCy}-artwork-card-line-3`}
		>
			{price}

			{#if previousPrice}
				<span class="ml-1 text-gray-500 line-through">{previousPrice}</span>
			{/if}
		</Txt></ArtworkCard
	>
</div>
